import React from 'react'
import _ from 'lodash'
import styled from 'styled-components'
import { withTranslation, Trans } from 'react-i18next'
import { rhythm, media } from '../utils/typography'
import Scroll from 'react-scroll'
import classnames from 'classnames'

import Layout from '../components/layout'
import Container from '../components/container'
import SEO from '../components/seo'
import Flag from '../components/flag'
import Moment from '../components/moment'
import External from '../components/external'
import Link from '../components/link'

export default styled(withTranslation()(({ className, t, pageContext }) => {
  const { country } = pageContext

  const mapClass = treaty => {
    if (!treaty || treaty.suspension) return 'absent'
    if (treaty.status) return 'inactive'
    return 'active'
  }

  const hasHkExtradition = _.find(country.treaties, treaty =>
    treaty.type === 'extradition' && treaty.dest === 'hk' && !treaty.suspension)
  const hasHkAssistance = _.find(country.treaties, treaty =>
    treaty.type === 'assistance' && treaty.dest === 'hk' && !treaty.suspension)
  const hasCnExtradition = _.find(country.treaties, treaty =>
    treaty.type === 'extradition' && treaty.dest === 'cn' && !treaty.suspension)
  const hasCnAssistance = _.find(country.treaties, treaty =>
    treaty.type === 'assistance' && treaty.dest === 'cn' && !treaty.suspension)
  const hasExtradition = hasHkExtradition || hasCnExtradition
  const hasAssistance = hasHkAssistance || hasCnAssistance

  const treaties = _.map(country.treaties, treaty => {
    treaty.klass = mapClass(treaty)
    return treaty
  })

  const translateDuration = d => {
    let match
    if (match = d.match(/^(\d+?)d$/)) {
      return t('duration.day', { num: match[1] })
    } else if (match = d.match(/^(\d+?)m$/)) {
      return t('duration.month', { num: match[1] })
    } else if (match = d.match(/^(\d+?)y$/)) {
      return t('duration.year', { num: match[1] })
    } else {
      return d
    }
  }

  const terminationPeriods = _.uniq(_.compact(
    _.map(treaties, treaty => (
      treaty.termination !== '0' && translateDuration(treaty.termination)
    ))))

  return (
    <Layout className={className}>
      <SEO title={t(`countries.${country.code}.short`)} />

      <Container>
        <h1>
          <Flag className='flag' country={country.code} />
          {t(`countries.${country.code}.short`)}
        </h1>
      </Container>

      <section className='hero'>
        <Container>
          <Trans i18nKey='extradition.country.hero'
            components={{
              highlight: <span className='highlight danger' />,
              country: t(`countries.${country.code}.short`),
              adjective: t(`countries.${country.code}.adjective`),
            }} />
        </Container>
      </section>

      <Container>
        <p><em>
          <Trans i18nKey='extradition.cta.jump'
            components={{
              jump: <Scroll.Link className='pointer' to='cta' smooth={true} />,
            }} />
        </em></p>

        <Trans i18nKey='extradition.intro'
          components={{
            nsl: <External href='https://www.gld.gov.hk/egazette/english/gazette/volume.php?year=2020&vol=24&no=44&extra=1&type=2' />,
          }} />
        <section className='quotation'>
          <Trans i18nKey='extradition.law.critic' />
        </section>

        <Trans i18nKey='extradition.country.global'
          values={{
            country: t(`countries.${country.code}.short`),
            people: t(`countries.${country.code}.people`),
          }} />
        <section className='quotation'>
          <Trans i18nKey='extradition.law.global' />
        </section>

        <Trans i18nKey='extradition.country.foreignRequest'
          values={{ adjective: t(`countries.${country.code}.adjective`) }} />
        <section className='quotation'>
          <Trans i18nKey='extradition.law.foreignRequest' />
        </section>

        <Trans i18nKey='extradition.country.gestapo'
          values={{
            adjective: t(`countries.${country.code}.adjective`),
            people: t(`countries.${country.code}.people`),
          }} />
        <section className='quotation'>
          <Trans i18nKey='extradition.law.gestapo' />
        </section>

        <Trans i18nKey='extradition.nslFullText'
          components={{
            localFile: <External href={t('extradition.nslFullTextFile')} />,
            localLink: <External href={t('extradition.nslFullTextLink')} />,
            hkFile: <External href='/files/nsl-hk.pdf' />,
            hkLink: <External href='https://www.gld.gov.hk/egazette/english/gazette/volume.php?year=2020&vol=24&no=44&extra=1&type=2' />,
            enFile: <External href='/files/nsl-english.doc' />,
            enLink: <External href='http://www.xinhuanet.com/english/2020-07/01/c_139178753.htm' />,
          }} />

        <h2>{t('extradition.country.foreignLaw.title',
          { adjective: t(`countries.${country.code}.adjective`) })}</h2>

        <p>
          {(hasHkExtradition && hasHkAssistance) && t('extradition.treaty.both',
            { country: t(`countries.${country.code}.short`), dest: t('countries.hk.short') })}
          {(hasHkExtradition && !hasHkAssistance) && t('extradition.treaty.extradition',
            { country: t(`countries.${country.code}.short`), dest: t('countries.hk.short') })}
          {(!hasHkExtradition && hasHkAssistance) && t('extradition.treaty.assistance',
            { country: t(`countries.${country.code}.short`), dest: t('countries.hk.short') })}
        </p>

        <p>
          {(hasCnExtradition && hasCnAssistance) && t('extradition.treaty.both',
            { country: t(`countries.${country.code}.short`), dest: t('countries.cn.short') })}
          {(hasCnExtradition && !hasCnAssistance) && t('extradition.treaty.extradition',
            { country: t(`countries.${country.code}.short`), dest: t('countries.cn.short') })}
          {(!hasCnExtradition && hasCnAssistance) && t('extradition.treaty.assistance',
            { country: t(`countries.${country.code}.short`), dest: t('countries.cn.short') })}
        </p>

        <Trans i18nKey='extradition.country.foreignLaw.body'
          values={{ adjective: t(`countries.${country.code}.adjective`) }} />

        <p>
          {(hasExtradition && hasAssistance) &&
            t('extradition.consequence.both', { adjective: t(`countries.${country.code}.adjective`) })}
          {(hasExtradition && !hasAssistance) &&
            t('extradition.consequence.extradition', { adjective: t(`countries.${country.code}.adjective`) })}
          {(!hasExtradition && hasAssistance) &&
            t('extradition.consequence.assistance', { adjective: t(`countries.${country.code}.adjective`) })}
        </p>

        {_.map(treaties, (treaty, key) =>
          <section className='treaty' key={key}>
            <h2>{t(`extradition.${treaty.type}.title`, { dest: t(`countries.${treaty.dest}.short`) })}</h2>

            <Trans i18nKey={`extradition.${treaty.type}.intro`}
              values={{
                adjective: t(`countries.${country.code}.adjective`),
                destAdjective: t(`countries.${treaty.dest}.adjective`),
                status: treaty.suspension ? t('extradition.status.suspended') :
                  (treaty.status ? t(`extradition.status.${treaty.status}`)
                    : t('extradition.status.active')),
              }}
              components={{
                year: <Moment date={treaty.date} format='YYYY' />,
                status: <span className={`status ${treaty.klass}`} />,
              }} />

            <section className='quotation details'>
              <p className={classnames({ inactive: treaty.suspension })}>
                {_.map(treaty.names, (name, key) =>
                  <span key={key} className='name'>{name.name}</span>
                )}
              </p>

              <p className={classnames({ inactive: treaty.suspension })}>
                {t('extradition.treaty.date')}{' '}
                <Moment date={treaty.date} format='YYYY-MM-DD' />
              </p>

              <p className={classnames({ inactive: treaty.suspension })}>
                {treaty.termination === '0'
                  ? t('extradition.treaty.termination.immediate')
                  : <Trans i18nKey='extradition.treaty.termination.normal'
                      values={{ termination: translateDuration(treaty.termination) }} />}
              </p>

              <p>
                {t('extradition.treaty.links')}{' '}
                {treaty.link &&
                  <External href={treaty.link}>{t(`countries.${treaty.dest}.short`)}</External>}
                {treaty.link && treaty.foreignLink && ', '}
                {treaty.foreignLink &&
                  <External href={treaty.foreignLink}>{t(`countries.${country.code}.short`)}</External>}
              </p>

              <p>
                {t('extradition.treaty.texts')}{' '}
                {treaty.file &&
                  <External href={`/files/${treaty.file}`}>{t(`countries.${treaty.dest}.short`)}</External>}
                {treaty.file && treaty.foreignFile && ', '}
                {treaty.foreignFile &&
                  <External href={`/files/${treaty.foreignFile}`}>{t(`countries.${country.code}.short`)}</External>}
              </p>

              {treaty.suspension &&
                <p>
                  {t('extradition.treaty.suspension')}{' '}
                  <External href={treaty.suspensionLink}>{t(`countries.${treaty.suspension}.short`)}</External>
                </p>
              }
            </section>

            {!treaty.suspension &&
              <Trans i18nKey={`extradition.${treaty.type}.details`}
                values={{
                  adjective: t(`countries.${country.code}.adjective`),
                  dest: t(`countries.${treaty.dest}.short`),
                  destAdjective: t(`countries.${treaty.dest}.adjective`),
                }} />
            }
          </section>
        )}

        <h2>{t('extradition.safeguard.title')}</h2>
        <Trans i18nKey='extradition.safeguard.body'
          values={{
            adjective: t(`countries.${country.code}.adjective`),
            people: t(`countries.${country.code}.people`)
          }}
          components={{
            simon: <External href='https://www.bbc.com/news/world-asia-china-50457262' />,
            cro: <External href='https://hongkongfp.com/2020/05/14/hong-kong-police-tortured-detainees-says-rights-group-as-un-experts-voice-concern-over-leading-activists-arrests/' />,
            meng: <External href='https://thediplomat.com/2020/05/canada-and-the-us-china-geopolitical-tug-of-war/' />,
            unlikely: <External href='https://www.ipac.global/news/statement-from-simon-o-connor-mp-and-louisa-wall-mp' />,
          }} />
      </Container>

      <Scroll.Element name='cta' />
      <section className='hero'>
        <Container>
          <Trans i18nKey='extradition.cta.hero'
            values={{ person: t(`countries.${country.code}.person`) }}
            components={{ highlight: <span className='highlight danger' /> }} />
        </Container>
      </section>

      <Container>
        <h2>{t('extradition.cta.title')}</h2>
        <Trans i18nKey='extradition.cta.intro'
          values={{ adjective: t(`countries.${country.code}.adjective`) }} />

        <h3>{t('extradition.general.title')}</h3>
        <ul>
          <li>{t('extradition.general.tweet')}</li>
          <li>{t('extradition.general.media')}</li>
          <li>{t('extradition.general.group')}</li>
          <li>
            <Trans i18nKey='extradition.general.signature'
              components={{ sign: <Link to='/nsl-statement' /> }} />
          </li>
          <li>
            <Trans i18nKey='extradition.general.map'
              components={{ map: <Link to='/extradition/map' /> }} />
          </li>
        </ul>

        <Trans i18nKey='extradition.general.list' />
        <ul>
          <li><External href='https://www.hongkongwatch.org/all-posts/2020/5/23/patten-led-group-of-198-parliamentarians-from-23-countries-decry-flagrant-breach-of-the-sino-british-joint-declaration'>
            {t('extradition.general.patten')}
          </External></li>
          <li><Link to='/nsl-statement'>
            {t('extradition.general.statement')}
          </Link></li>
          <li><External href='https://www.ipac.global/team'>
            {t('extradition.general.ipac')}
          </External></li>
        </ul>

        <h3>{t('extradition.politicians.title')}</h3>
        <Trans i18nKey='extradition.politicians.intro'
          values={{
            person: t(`countries.${country.code}.person`),
            people: t(`countries.${country.code}.people`),
            adjective: t(`countries.${country.code}.adjective`),
          }} />

        <ul>
          <li>
            <Trans i18nKey='extradition.politicians.1c2s'
              values={{
                country: t(`countries.${country.code}.short`),
              }}
              components={{
                npc: <External href='http://www.xinhuanet.com/english/2020-06/30/c_139178353_2.htm' />,
                hk: <External href='https://www.gld.gov.hk/egazette/english/gazette/volume.php?year=2020&vol=24&no=44&extra=1&type=2' />,
                us: <External href='https://www.state.gov/prc-national-peoples-congress-proposal-on-hong-kong-national-security-legislation/' />,
                uk: <External href='https://www.bbc.com/news/uk-politics-53246899' />,
                canada: <External href='https://www.cbc.ca/news/politics/canada-suspending-extradition-treaty-hong-kong-over-security-law-1.5636479' />,
                aus: <External href='https://www.smh.com.au/world/asia/australia-suspends-hong-kong-extradition-treaty-extends-hong-kongers-visas-after-security-law-imposed-20200709-p55ak8.html' />,
              }} />
          </li>

          <li>
            {(hasExtradition && hasAssistance) && t('extradition.politicians.terminate.both')}
            {(hasExtradition && !hasAssistance) && t('extradition.politicians.terminate.extradition')}
            {(!hasExtradition && hasAssistance) && t('extradition.politicians.terminate.assistance')}
          </li>

          <li>
            {terminationPeriods.length
              ? t('extradition.politicians.localLaw.termination', {
                  adjective: t(`countries.${country.code}.adjective`),
                  people: t(`countries.${country.code}.people`),
                  termination: terminationPeriods.join(' / '),
                })
              : t('extradition.politicians.localLaw.normal', {
                  adjective: t(`countries.${country.code}.adjective`),
                })
            }
          </li>

          <li>
            <Trans i18nKey='extradition.politicians.travel'
              components={{ nsl: <External href={t('extradition.nslFullTextLink')} /> }} />
          </li>

          <li>
            <Trans i18nKey='extradition.politicians.signature'
              components={{ sign: <Link to='/nsl-statement' /> }} />
          </li>
        </ul>

        <h3>{t('extradition.media.title')}</h3>
        <Trans i18nKey='extradition.media.intro'
          values={{
            people: t(`countries.${country.code}.people`),
            adjective: t(`countries.${country.code}.adjective`),
          }}
          components={{ nsl: <External href={t('extradition.nslFullTextLink')} /> }} />

        <ul>
          <li>
            <Trans i18nKey='extradition.media.nsl'
              components={{ nsl: <External href={t('extradition.nslFullTextLink')} /> }} />
          </li>

          <li>
            {(hasExtradition && hasAssistance) && t('extradition.media.treaty.both')}
            {(hasExtradition && !hasAssistance) && t('extradition.media.treaty.extradition')}
            {(!hasExtradition && hasAssistance) && t('extradition.media.treaty.assistance')}
          </li>
        </ul>

        <h3>{t('extradition.academia.title')}</h3>
        <Trans i18nKey='extradition.academia.intro'
          components={{ nsl: <External href={t('extradition.nslFullTextLink')} /> }} />

        <ul>
          <li>{t('extradition.academia.global')}</li>
          <li>{t('extradition.academia.extradition')}</li>
          <li>{t('extradition.academia.1c2s')}</li>
          <li>{t('extradition.academia.failure')}</li>
        </ul>
      </Container>

      <section className='hero'>
        <Container>
          <Trans i18nKey='extradition.cta.closing'
            components={{ highlight: <span className='highlight danger' /> }} />
        </Container>
      </section>
    </Layout>
  )
}))`
  .flag {
    display: inline-block;
    height: ${rhythm(0.9 * 3/4)};
    ${media.tablet} { height: ${rhythm(0.9)} }
    margin-right: ${rhythm(1/4)};
  }

  h1 {
    font-size: ${rhythm(3/4)};
    ${media.tablet} { font-size: ${rhythm(1)} }
    margin: ${rhythm(1)} 0 ${rhythm(1/4)};
  }

  .status {
    display: inline-block;
    font-size: ${rhythm(1/2)};
    text-transform: lowercase;
    padding: ${rhythm(1/8)} ${rhythm(1/4)};
    border-radius: ${rhythm(1/5)};
    &.absent { background: #87de87; }
    &.inactive { background: #ffeeaa; }
    &.active { background: #ffaaaa; }
  }

  .details {
    .name {
      display: block;
    }
    .inactive {
      text-decoration: line-through;
    }
  }
`
